import React from 'react';
import {
  Dialog,
  Box,
  Typography,
  CircularProgress,
  Fade,
  Button,
  Stack,
} from '@mui/material';


const ConfirmationModal = ({ 
  open, 
  status, 
  title, 
  subtitle, 
  userName = '',
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  onConfirm,
  onCancel,
  isDangerous = false
}) => {
  // status puede ser: 'creating', 'success', 'error', 'confirm'
  
  const getContent = () => {
    switch (status) {
      case 'loading':
      case 'creating':
        return {
          icon: (
            <CircularProgress 
              size={48} 
              sx={{ 
                color: '#111827',
                marginBottom: 2,
              }} 
            />
          ),
          title: title || 'Procesando',
          subtitle: subtitle || 'Por favor espera mientras procesamos la información...',
          bgColor: '#ffffff',
        };
      
      case 'success':
        return {
          icon: (
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: '#111827',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 2,
              }}
            >
              <Typography sx={{ color: '#ffffff', fontSize: '1.5rem', fontWeight: 'bold' }}>
                ✓
              </Typography>
            </Box>
          ),
          title: title || '¡Completado!',
          subtitle: subtitle || (userName ? `${userName} ha sido procesado exitosamente` : 'La operación se completó exitosamente'),
          bgColor: '#ffffff',
        };
      
      case 'error':
        return {
          icon: (
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: '#ffffff',
                border: '2px solid #111827',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 2,
              }}
            >
              <Typography sx={{ color: '#111827', fontSize: '1.5rem', fontWeight: 'bold' }}>
                ✕
              </Typography>
            </Box>
          ),
          title: title || 'Error',
          subtitle: subtitle || 'Hubo un problema al procesar la solicitud',
          bgColor: '#ffffff',
        };
      
      case 'confirm':
        return {
          icon: (
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: '#f9fafb',
                border: '2px solid #111827',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 2,
              }}
            >
              <Typography sx={{ color: '#111827', fontSize: '1.5rem', fontWeight: 'bold' }}>
                ⚠
              </Typography>
            </Box>
          ),
          title: title || '¿Confirmar acción?',
          subtitle: subtitle || 'Esta acción requiere confirmación',
          bgColor: '#ffffff',
        };
      
      default:
        return {
          icon: null,
          title: '',
          subtitle: '',
          bgColor: '#ffffff',
        };
    }
  };

  const content = getContent();

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          backgroundColor: content.bgColor,
          border: '1px solid #e5e7eb',
        },
      }}
      // Permitir cerrar solo si no está cargando y hay función onCancel
      disableEscapeKeyDown={status === 'creating' || status === 'loading'}
      onClose={status === 'confirm' && onCancel ? onCancel : () => {}}
    >
      <Box
        sx={{
          padding: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          minHeight: '200px',
          justifyContent: 'center',
        }}
      >
        {/* Icono con animación */}
        <Fade in={true} timeout={500}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            {content.icon}
          </Box>
        </Fade>

        {/* Título */}
        <Fade in={true} timeout={700}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: '#111827',
              marginBottom: 1,
              fontSize: '1.5rem',
            }}
          >
            {content.title}
          </Typography>
        </Fade>

        {/* Subtítulo */}
        <Fade in={true} timeout={900}>
          <Typography
            variant="body1"
            sx={{
              color: '#6b7280',
              fontSize: '0.95rem',
              lineHeight: 1.5,
              maxWidth: '280px',
            }}
          >
            {content.subtitle}
          </Typography>
        </Fade>

        {/* Puntos de loading animados solo cuando está cargando */}
        {(status === 'creating' || status === 'loading') && (
          <Box sx={{ marginTop: 3, display: 'flex', gap: 0.5 }}>
            {[0, 1, 2].map((index) => (
              <Box
                key={index}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#d1d5db',
                  animation: `pulse 1.5s ease-in-out ${index * 0.2}s infinite`,
                  '@keyframes pulse': {
                    '0%, 80%, 100%': {
                      backgroundColor: '#d1d5db',
                      transform: 'scale(1)',
                    },
                    '40%': {
                      backgroundColor: '#111827',
                      transform: 'scale(1.2)',
                    },
                  },
                }}
              />
            ))}
          </Box>
        )}

        {/* Botones de confirmación (solo para status 'confirm') */}
        {status === 'confirm' && (
          <Fade in={true} timeout={1100}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ marginTop: 3, width: '100%', maxWidth: '280px' }}
            >
              <Button
                variant="outlined"
                onClick={onCancel}
                sx={{
                  flex: 1,
                  height: 44,
                  borderColor: '#e5e7eb',
                  color: '#6b7280',
                  '&:hover': {
                    borderColor: '#d1d5db',
                    backgroundColor: '#f9fafb',
                  },
                }}
              >
                {cancelText}
              </Button>
                              <Button
                  variant="contained"
                  onClick={onConfirm}
                  sx={{
                    flex: 1,
                    height: 44,
                    backgroundColor: '#111827',
                    '&:hover': {
                      backgroundColor: '#374151',
                    },
                  }}
                >
                {confirmText}
              </Button>
            </Stack>
          </Fade>
        )}
      </Box>
    </Dialog>
  );
};

export default ConfirmationModal; 